import { createApi } from '@reduxjs/toolkit/query/react'
import { BaseRequest, BaseResponse } from '../models'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { paginate } from '../utils'
import { EmailMetadata, EmailMetadataResponseProcessing, EmailMetadataTypeProcessing } from '../../models/EmailMetadata'
import { removeEmptyItems } from '../../common/util'

const _BASE_URL = import.meta.env.REACT_APP_INTEGRATIONS_SERVICE_API_BASE_URL!

export interface GetEmailMetadataRequest extends BaseRequest {
  emailConnector: string
  externalId: string
}

export interface QueryEmailMetadataRequest extends BaseRequest {
  emailConnector: string
  externalIds?: string[]
}

export interface UpdateEmailMetadataPayload {
  threadId?: string
  senderEmail?: string
  typeProcessing?: EmailMetadataTypeProcessing
  responseProcessing?: EmailMetadataResponseProcessing
  processedByUser?: boolean
}

export interface UpdateEmailMetadataRequest extends BaseRequest {
  emailConnector: string
  externalId: string
  payload: UpdateEmailMetadataPayload
  removeEmptyValues?: boolean
}

export const emailMetadataApi = createApi({
  reducerPath: 'emailMetadataApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['EmailMetadata'],
  endpoints: (builder) => ({
    getEmailMetadata: builder.query<EmailMetadata, GetEmailMetadataRequest>({
      query: (request) => {
        const { accountId, emailConnector, externalId } = request

        return {
          url: `/accounts/${accountId}/email-connectors/${emailConnector}/metadata/${externalId}`,
          validateStatus: (response) => (response.status >= 200 && response.status <= 299) || response.status === 404,
        }
      },
      transformResponse: (response: BaseResponse<EmailMetadata>) => {
        return response.data
      },
      providesTags: ['EmailMetadata'],
    }),
    queryEmailMetadata: builder.query<BaseResponse<EmailMetadata[]>, QueryEmailMetadataRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        const { accountId, emailConnector, externalIds } = request
        return await paginate<EmailMetadata>(
          {
            url: `/accounts/${accountId}/email-connectors/${emailConnector}/metadata/query`,
            method: 'POST',
            body: { ids: externalIds },
          },
          fetchWithBQ,
        )
      },
      providesTags: ['EmailMetadata'],
    }),
    updateEmailMetadata: builder.mutation<EmailMetadata, UpdateEmailMetadataRequest>({
      query: (request) => {
        const { accountId, emailConnector, externalId, payload, removeEmptyValues = true } = request
        return {
          url: `/accounts/${accountId}/email-connectors/${emailConnector}/metadata/${externalId}`,
          method: 'PUT',
          body: removeEmptyValues ? removeEmptyItems(payload) : payload,
        }
      },
      transformResponse: (response: BaseResponse<EmailMetadata>) => {
        return response.data
      },
      invalidatesTags: ['EmailMetadata'],
    }),
  }),
})

export const { useLazyGetEmailMetadataQuery, useLazyQueryEmailMetadataQuery, useUpdateEmailMetadataMutation } = emailMetadataApi
